import { useCallback, useMemo } from "react";

import {
  RESOURCE_CREATED_EVENT,
  USER_INTERACTION_EVENT,
  CREATE_NEW_TABLE_BUTTON_TEXT,
  CREATE_NEW_INTERFACE_BUTTON_TEXT,
  CREATE_NEW_ZAP_BUTTON_TEXT,
  CREATE_NEW_CHATBOTS_BUTTON_TEXT,
  CREATE_NEW_AGENT_BUTTON_TEXT,
} from "@zapier/canvas-constants";
import { uuid } from "./utils/uuid";

export function useEmitResourceCreatedEvents({
  canvasId,
  sessionId,
  currentAccountId,
}: {
  canvasId: string;
  sessionId: number | undefined;
  currentAccountId: number | undefined;
}) {
  const commonProps = useMemo(
    () => ({
      customuser_id: sessionId,
      account_id: currentAccountId,
    }),
    [currentAccountId, sessionId],
  );

  const commonPropsInteractionEvent = useMemo(
    () => ({
      system_name: "canvas",
      canvas_id: canvasId,
      ...commonProps,
    }),
    [canvasId, commonProps],
  );

  const emitEventOnZapCreation = useCallback(
    (zapId: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "click",
            interaction_goal: "Create zap draft",
            interaction_raw_text: CREATE_NEW_ZAP_BUTTON_TEXT,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );

        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Zap",
            resource_id: zapId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CREATED_EVENT,
        );
      }
    },
    [commonPropsInteractionEvent, commonProps],
  );

  const emitEventOnTableCreation = useCallback(
    (tableId: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "click",
            interaction_goal: "Create Zapier Table",
            interaction_raw_text: CREATE_NEW_TABLE_BUTTON_TEXT,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );

        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Tables",
            resource_id: tableId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CREATED_EVENT,
        );
      }
    },
    [commonPropsInteractionEvent, commonProps],
  );

  const emitEventOnInterfaceCreation = useCallback(
    (projectId: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "click",
            interaction_goal: "Create Zapier Interface",
            interaction_raw_text: CREATE_NEW_INTERFACE_BUTTON_TEXT,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );

        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Interfaces",
            resource_id: projectId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CREATED_EVENT,
        );
      }
    },
    [commonPropsInteractionEvent, commonProps],
  );

  const emitEventOnChatbotCreation = useCallback(
    (chatbotId: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "click",
            interaction_goal: "Create Zapier Chatbot",
            interaction_raw_text: CREATE_NEW_CHATBOTS_BUTTON_TEXT,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Chatbots",
            resource_id: chatbotId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CREATED_EVENT,
        );
      }
    },
    [commonProps, commonPropsInteractionEvent],
  );

  const emitEventOnAgentCreation = useCallback(
    (agentId: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "click",
            interaction_goal: "Create Zapier Agent",
            interaction_raw_text: CREATE_NEW_AGENT_BUTTON_TEXT,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );

        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Agents",
            resource_id: agentId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CREATED_EVENT,
        );
      }
    },
    [commonPropsInteractionEvent, commonProps],
  );

  return {
    emitEventOnZapCreation,
    emitEventOnTableCreation,
    emitEventOnInterfaceCreation,
    emitEventOnChatbotCreation,
    emitEventOnAgentCreation,
  };
}
